import React from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { useSubscriptionUsage } from 'context/subscription-usage'
import {
    ACCOUNT_CHECKOUT_PATH,
    ACCOUNT_PRICING_PATH,
    ENABLE_FREE_PLAN_USAGE_UI,
    ENABLE_LICENSE_SHOPPING_CART,
    FREE_MAX_ACTIVE_TEMPLATES,
    FREE_MAX_SEATS,
    barWidthPercent,
    formatUsageLabel,
    getBarTone,
    getWorstTone,
    isFreeTier,
} from 'utility/subscription-usage'
import { PlanLimitBanner } from './PlanLimitBanner'

const Card = styled.div`
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 20px;
    margin-bottom: 16px;
    @media (max-width: 930px) {
        padding: 16px;
    }
`

const CardHd = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;
`

const CardTitle = styled.div`
    font-size: 15px;
    font-weight: 700;
    color: #111827;
`

const PlanChip = styled.span`
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    &.free {
        background: #eff6ff;
        border: 1px solid #bfdbfe;
        color: #1d4ed8;
    }
    &.premium {
        background: #ecfdf5;
        border: 1px solid #a7f3d0;
        color: #047857;
    }
    &.enterprise {
        background: #f5f3ff;
        border: 1px solid #ddd6fe;
        color: #6d28d9;
    }
    &.unknown {
        background: #f3f4f6;
        border: 1px solid #d1d5db;
        color: #4b5563;
    }
`

const UsageGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    @media (max-width: 930px) {
        grid-template-columns: 1fr;
        gap: 12px;
    }
`

const UsageCell = styled.div`
    padding: 14px 16px;
    background: #f9fafb;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    .lbl {
        font-size: 12px;
        color: #6b7280;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }
    .num {
        font-size: 18px;
        font-weight: 700;
        color: #111827;
        margin-top: 6px;
        font-variant-numeric: tabular-nums;
    }
    .num .cap {
        color: #6b7280;
        font-weight: 500;
        font-size: 16px;
    }
    .num .unlim {
        color: #6b7280;
        font-weight: 500;
        font-size: 14px;
        margin-left: 6px;
    }
    .bar {
        margin-top: 8px;
        height: 6px;
        background: #e5e7eb;
        border-radius: 999px;
        overflow: hidden;
    }
    .bar-fill {
        height: 100%;
        border-radius: 999px;
    }
    .bar-fill.ok {
        background: #2563eb;
    }
    .bar-fill.warn {
        background: #f59e0b;
    }
    .bar-fill.over {
        background: #dc2626;
    }
    .bar-fill.unlim {
        background: #10b981;
    }
`

const Actions = styled.div`
    margin-top: 16px;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    @media (max-width: 930px) {
        flex-direction: column;
        button {
            width: 100%;
        }
    }
`

const AboutCard = styled(Card)`
    p {
        font-size: 13px;
        color: #6b7280;
        line-height: 1.6;
        margin: 0;
    }
`

const UnavailableNote = styled.div`
    font-size: 12px;
    color: #6b7280;
    margin-bottom: 12px;
`

const MetricCell = ({ label, metric }) => {
    const formatted = formatUsageLabel(metric)
    const tone = getBarTone(metric)
    return (
        <UsageCell>
            <div className="lbl">{label}</div>
            <div className="num">
                {formatted.current}
                {formatted.capped ? (
                    <span className="cap"> {formatted.suffix}</span>
                ) : (
                    <span className="unlim">{formatted.suffix}</span>
                )}
            </div>
            <div className="bar">
                <div
                    className={`bar-fill ${tone}`}
                    style={{ width: `${barWidthPercent(metric)}%` }}
                />
            </div>
        </UsageCell>
    )
}

const chipClassForTier = (tier, planName) => {
    if (tier === 'Free') return 'free'
    if (tier === 'Enterprise') return 'enterprise'
    if (tier === 'Premium') return 'premium'
    if (tier === 'Unknown') return 'unknown'
    if (planName) return 'premium'
    return 'unknown'
}

const chipLabel = (tier, planName) => {
    if (tier === 'Unknown') return '—'
    if (tier === 'Premium' && planName) return planName
    return tier || planName || '—'
}

/** When PaymentStatus says unpaid, do not trust premature Yearly/Unlimited Usage. */
const asFreeDisplayUsage = usage => {
    if (!usage) return usage
    const seatsCurrent = Number(usage.seats?.current ?? 0)
    const templatesCurrent = Number(usage.activeTemplates?.current ?? 0)
    const seats = {
        current: seatsCurrent,
        cap: FREE_MAX_SEATS,
        isOver: seatsCurrent > FREE_MAX_SEATS,
    }
    const activeTemplates = {
        current: templatesCurrent,
        cap: FREE_MAX_ACTIVE_TEMPLATES,
        isOver: templatesCurrent > FREE_MAX_ACTIVE_TEMPLATES,
    }
    return {
        ...usage,
        planName: 'Free',
        tier: 'Free',
        seats,
        activeTemplates,
        isOverAnyLimit: seats.isOver || activeTemplates.isOver,
    }
}

export const PlanUsagePanel = ({
    showAbout = true,
    showBanner = true,
    /** When false, hide Upgrade / See other plans (e.g. Account page already has billing CTA). */
    showUpgradeActions = true,
    /** When false, banner is warning-only (no Upgrade button). */
    showBannerUpgrade = true,
    onUpgrade = null,
    /**
     * When true, force Free chip + Free caps even if Usage says Premium/Yearly
     * (unpaid after PurchaseOrder without PaymentConfirm).
     */
    forceFree = false,
}) => {
    const navigate = useNavigate()
    const { usage: rawUsage, loading } = useSubscriptionUsage()
    if (!ENABLE_FREE_PLAN_USAGE_UI) return null
    if (!rawUsage && loading) return null
    if (!rawUsage) return null

    const usage = forceFree ? asFreeDisplayUsage(rawUsage) : rawUsage
    const free = isFreeTier(usage.tier)
    const worst = getWorstTone(usage)
    const showUpgrade =
        ENABLE_LICENSE_SHOPPING_CART && free && showUpgradeActions
    const openUpgradeFlow = () => {
        if (typeof onUpgrade === 'function') {
            onUpgrade()
            return
        }
        navigate(ACCOUNT_CHECKOUT_PATH)
    }
    const openPricingPage = () => navigate(ACCOUNT_PRICING_PATH)

    return (
        <>
            {showBanner && free && (worst === 'warn' || worst === 'over') && (
                <PlanLimitBanner
                    onUpgrade={onUpgrade || undefined}
                    showUpgradeButton={showBannerUpgrade}
                />
            )}
            <Card>
                <CardHd>
                    <CardTitle>Plan Usage</CardTitle>
                    <PlanChip
                        className={chipClassForTier(usage.tier, usage.planName)}
                    >
                        {chipLabel(usage.tier, usage.planName)}
                    </PlanChip>
                </CardHd>
                {usage.tier === 'Unknown' && (
                    <UnavailableNote>Plan details unavailable</UnavailableNote>
                )}
                <UsageGrid>
                    <MetricCell label="Seats" metric={usage.seats} />
                    <MetricCell
                        label="Active templates"
                        metric={usage.activeTemplates}
                    />
                </UsageGrid>
                {showUpgrade && (
                    <Actions>
                        <AppPrimaryButton
                            type="button"
                            onClick={openUpgradeFlow}
                        >
                            Upgrade Plan
                        </AppPrimaryButton>
                        <AppSecondaryButton
                            type="button"
                            onClick={openPricingPage}
                        >
                            See other plans
                        </AppSecondaryButton>
                    </Actions>
                )}
            </Card>
            {showAbout && free && (
                <AboutCard>
                    <CardTitle style={{ marginBottom: 10 }}>
                        About your plan
                    </CardTitle>
                    <p>
                        Your Free plan includes unlimited inspections and up to
                        10 seats and 5 active templates. Upgrade any time for
                        unlimited users and templates.
                    </p>
                </AboutCard>
            )}
        </>
    )
}
